/**
 * cockpitNettoCash — PURE Netto-Cash-Vorschau-Modell (Design-Identität v5
 * „Kontor", `architecture-ux-design-v5.md` §6.3, Arbeitspaket M-D5-C2).
 *
 * Ersetzt die alte `cockpitLiquidity#forecastIncoming`-Kachel (die nur die
 * offenen Forderungen nach Datum bucketete = Aging-Duplikat). NettoCash bildet
 * eine ECHTE Wochenvorschau: erwartete Zahlungs-EINGÄNGE (DSO-korrigierte
 * Forderungen) minus bekannte Zahlungs-AUSGÄNGE (Lieferanten-Ausgaben + USt-
 * Zahllast) je Woche, plus eine kumulierte Netto-Linie ab 0.
 *
 * DISCIPLINE (Muster `utils/cockpitLiquidity.ts` / `utils/cockpitReceivables.ts`):
 * kein Firestore-/React-Import — nur Typ-Importe (compile-time erased). Total
 * function: JEDE Eingabe-Kombination liefert genau ein {@link NettoCashResult}
 * (nie eine Exception, leere Eingaben ⇒ `horizonWeeks` Nullwochen). Alle
 * Geldbeträge Integer-Cents. Alle Datumsrechnungen über UTC-Mitternachts-Epochen
 * (kein DST-Drift); malformed Daten werden defensiv ignoriert statt zu
 * NaN/0-Poisoning zu führen. Die Datums-Helfer sind bewusst dupliziert (kein
 * Cross-Import winziger interner Helfer — etablierte Konvention in diesem
 * Modul-Layer).
 *
 * CFO-Auflagen (bindend, §6.3):
 *  - EIN: offene Forderungen je erwartetem Zahl-Datum. `expectedPay =
 *    max(invoiceDate + dsoDays, dueDate)` bei `DsoResult.kind==='ok'`; sonst
 *    nominelle Fälligkeit. Bereits überfällige Posten: `today + max(7, dsoDays −
 *    alter)`, geclampt in Woche 1..horizon — NIE ein „gestern"-Eingang.
 *  - AUS(1): Ausgaben NUR `status==='booked'` UND `dueDate>=today` (Heuristik
 *    gegen Doppelzählung bereits bezahlter Alt-Belege — `expenseTypes` kennt kein
 *    paid-Feld). `grossCents` als Abfluss in der dueDate-Woche. draft zählt NICHT.
 *  - AUS(2): USt-Zahllast `vatReturn.payableCents` als Abfluss in der Woche von
 *    `ustvaDeadlineISO`. `vatReturn===null` ⇒ USt-Abfluss überall 0 (der Aufrufer
 *    zeigt dann den „ohne USt-Abfluss"-Hinweis). Nur positive Zahllast ist
 *    Abfluss; Erstattung (≤0) wird NICHT als Zufluss gebucht (konservativ).
 *  - KEIN absoluter Kontostand: die Netto-Linie kumuliert ab 0.
 *
 * @module utils/cockpitNettoCash
 */

import type { DsoResult } from './cockpitReceivables';

// ─── Datums-Helfer (bewusst dupliziert, Muster `cockpitReceivables.ts`) ──────

const MS_PER_DAY = 86_400_000;

/**
 * Validates an ISO date string strictly enough to avoid NaN/garbage comparisons
 * downstream. A malformed value is treated as "unknown" — the caller MUST ignore
 * the entry (never coerce to `0`/`NaN`).
 */
function isValidISODate(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return false;
  const month = Number(m[2]);
  const day = Number(m[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

/** UTC-midnight epoch (ms) for an ISO `YYYY-MM-DD` string — no DST drift. */
function utcEpochOf(dateISO: string): number {
  const y = Number(dateISO.slice(0, 4));
  const m = Number(dateISO.slice(5, 7));
  const d = Number(dateISO.slice(8, 10));
  return Date.UTC(y, m - 1, d);
}

/** Whole calendar days between two ISO dates (`to - from`), UTC-epoch-based. */
function daysBetweenUTC(fromISO: string, toISO: string): number {
  return Math.round((utcEpochOf(toISO) - utcEpochOf(fromISO)) / MS_PER_DAY);
}

/** Formats a UTC-midnight epoch back to an ISO `YYYY-MM-DD` string. */
function isoFromEpoch(epochMs: number): string {
  const d = new Date(epochMs);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** ISO date `days` after `iso` (UTC-epoch-based). */
function addDaysISO(iso: string, days: number): string {
  return isoFromEpoch(utcEpochOf(iso) + days * MS_PER_DAY);
}

/** Normalises a possibly time-carrying value to a plain `YYYY-MM-DD` or null. */
function toDateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const sliced = value.slice(0, 10);
  return isValidISODate(sliced) ? sliced : null;
}

// ─── Eingabe-/Ausgabe-Typen (§6.3) ───────────────────────────────────────────

/**
 * Minimale Forderungs-Zeile für die EIN-Seite. `CockpitOpenItemRow`
 * (`loaders/cockpitLoaders`) ist strukturell zuweisbar — bewusst NUR die vier
 * benötigten Felder, damit das Modell entkoppelt + trivial testbar bleibt.
 */
export interface NettoCashOpenItem {
  status: 'open' | 'partially_paid' | 'paid' | 'overdue';
  /** Offener Restbetrag in Integer-Cents. */
  openAmountCents: number;
  /** Rechnungsdatum (BT-2) `YYYY-MM-DD` — Basis des DSO-korrigierten Zahl-Datums. */
  invoiceDateISO: string;
  /** Fälligkeit (BT-9) `YYYY-MM-DD` oder null. */
  dueDateISO: string | null;
}

/**
 * Minimale Ausgaben-Zeile für die AUS(1)-Seite. `Expense`
 * (`types/expenseTypes`) ist strukturell zuweisbar.
 */
export interface NettoCashExpense {
  status: 'draft' | 'booked';
  /** Fälligkeit (BT-9) `YYYY-MM-DD` oder null — nur `>=today` zählt als Abfluss. */
  dueDate: string | null;
  /** Brutto-Abfluss in Integer-Cents. */
  grossCents: number;
}

/** Eine Woche der Netto-Cash-Vorschau. Alle Beträge Integer-Cents. */
export interface NettoCashWeek {
  /** ISO-Datum (`YYYY-MM-DD`) des Wochenbeginns (= `today + i*7` Tage). */
  weekStartISO: string;
  /** Erwartete Zahlungseingänge (DSO-korrigierte Forderungen). */
  inflowCents: number;
  /** Bekannte Zahlungsausgänge (Lieferanten-Ausgaben + USt-Zahllast). */
  outflowCents: number;
  /** `inflowCents − outflowCents`. */
  netCents: number;
  /** Kumulierte Netto-Linie ab 0 (Σ `netCents` bis einschließlich dieser Woche). */
  cumulativeNetCents: number;
  /** Anteil USt am `outflowCents` dieser Woche (fürs Tooltip „am 10. geht USt raus"). */
  vatOutflowCents: number;
}

/** Ergebnis von {@link computeNettoCash}. */
export interface NettoCashResult {
  /** `horizonWeeks` Wochen in fester Reihenfolge (auch bei leeren Eingaben — Nullwochen). */
  weeks: NettoCashWeek[];
  /** Angewandter Horizont in Wochen (4..13, geclampt). */
  horizonWeeks: number;
  /** Angewandter DSO-Shift (Ø Zahlungsdauer); `null` = kein DSO ⇒ nominelle Fälligkeit. */
  dsoShiftDays: number | null;
}

/** Eingabe von {@link computeNettoCash}. */
export interface ComputeNettoCashInput {
  /** Offene Forderungen (`loadOpenItemRows`, Bestand — bereits gebubbelt). */
  openItems: NettoCashOpenItem[];
  /** DSO-Kennzahl (`computeDso`, Bestand). `kind!=='ok'` ⇒ nominelle Fälligkeit. */
  dso: DsoResult;
  /** Ausgaben (`getExpensesForUser`, Bestand — bereits gebubbelt). */
  expenses: NettoCashExpense[];
  /** `buildVatReturn`-Ergebnis (fail-closed null); nur positive Zahllast ist Abfluss. */
  vatReturn: { payableCents: number } | null;
  /** `computeNextUstvaDeadline`-Termin (ISO) — die Woche des USt-Abflusses. */
  ustvaDeadlineISO: string | null;
  /** Heutiges Datum `YYYY-MM-DD`. */
  todayISO: string;
  /** Horizont in Wochen (Default 8, geclampt 4..13). */
  horizonWeeks?: number;
}

// ─── Konstanten ──────────────────────────────────────────────────────────────

const DEFAULT_HORIZON_WEEKS = 8;
const MIN_HORIZON_WEEKS = 4;
const MAX_HORIZON_WEEKS = 13;
const DAYS_PER_WEEK = 7;
/** Mindest-Vorlauf für einen überfällig-forward-geschobenen Eingang (§6.3). */
const MIN_OVERDUE_LEAD_DAYS = DAYS_PER_WEEK;

/** Clampt den Horizont auf 4..13 Wochen (Default 8 bei fehlend/ungültig). */
function clampHorizon(weeks: number | undefined): number {
  if (typeof weeks !== 'number' || !Number.isFinite(weeks)) return DEFAULT_HORIZON_WEEKS;
  const rounded = Math.round(weeks);
  if (rounded < MIN_HORIZON_WEEKS) return MIN_HORIZON_WEEKS;
  if (rounded > MAX_HORIZON_WEEKS) return MAX_HORIZON_WEEKS;
  return rounded;
}

/**
 * Bestimmt den Wochen-Index (0-basiert) des erwarteten Zahl-Datums einer offenen
 * Forderung. `null` ⇒ die Zeile lässt sich nicht platzieren (kein Datum) ODER
 * fällt als zukünftiger Eingang hinter den Horizont (nicht angezeigt). Bereits
 * überfällige Posten werden nach vorn in Woche 1..horizon geschoben (nie in die
 * Vergangenheit), zukünftige jenseits des Horizonts entfallen.
 */
function inflowWeekIndex(
  row: NettoCashOpenItem,
  dso: DsoResult,
  todayISO: string,
  horizon: number,
): number | null {
  const invISO = toDateOnly(row.invoiceDateISO);
  const dueISO = toDateOnly(row.dueDateISO);
  const dsoOk = dso?.kind === 'ok';

  // Erwartetes Zahl-Datum (nominell) ─ CFO-Auflage EIN.
  let expectedISO: string | null;
  if (dsoOk && invISO) {
    const dsoDate = addDaysISO(invISO, dso.dsoDays);
    // expectedPay = max(invoiceDate + dsoDays, dueDate)
    expectedISO = dueISO ? (dsoDate >= dueISO ? dsoDate : dueISO) : dsoDate;
  } else {
    // ohne DSO (oder ohne Rechnungsdatum): nominelle Fälligkeit.
    expectedISO = dueISO;
  }
  if (expectedISO === null) return null;

  // Bereits überfällig (erwartetes Datum in der Vergangenheit) ⇒ forward-clamp.
  if (expectedISO < todayISO) {
    let leadDays = MIN_OVERDUE_LEAD_DAYS;
    if (dsoOk && invISO) {
      const alter = Math.max(0, daysBetweenUTC(invISO, todayISO));
      leadDays = Math.max(MIN_OVERDUE_LEAD_DAYS, dso.dsoDays - alter);
    }
    let idx = Math.floor(leadDays / DAYS_PER_WEEK);
    if (idx < 0) idx = 0;
    if (idx >= horizon) idx = horizon - 1; // in den Horizont clampen (nicht verwerfen)
    return idx;
  }

  // Zukünftiger Eingang.
  const daysOut = daysBetweenUTC(todayISO, expectedISO);
  const idx = Math.floor(daysOut / DAYS_PER_WEEK);
  if (idx < 0) return 0;
  if (idx >= horizon) return null; // jenseits des Horizonts ⇒ nicht angezeigt
  return idx;
}

/**
 * Berechnet die Netto-Cash-Vorschau (Wochen-EIN/AUS + kumulierte Netto-Linie)
 * aus den bereits gebubbelten Cockpit-Rohdaten. Siehe Modul-Kopf für die
 * bindenden CFO-Auflagen.
 *
 * TOTAL (wirft nie): leere/ungültige Eingaben ⇒ `horizonWeeks` Nullwochen.
 *
 * @param input - {@link ComputeNettoCashInput}.
 * @returns {@link NettoCashResult}.
 */
export function computeNettoCash(input: ComputeNettoCashInput): NettoCashResult {
  const horizon = clampHorizon(input.horizonWeeks);
  const today = input.todayISO;
  const dsoShiftDays = input.dso?.kind === 'ok' ? input.dso.dsoDays : null;
  const todayValid = isValidISODate(today);

  // Wochen initialisieren (Nullwochen). weekStartISO best-effort auch bei
  // ungültigem `today` (dann leerer String — die Beträge bleiben 0).
  const weeks: NettoCashWeek[] = [];
  for (let i = 0; i < horizon; i++) {
    weeks.push({
      weekStartISO: todayValid ? addDaysISO(today, i * DAYS_PER_WEEK) : '',
      inflowCents: 0,
      outflowCents: 0,
      netCents: 0,
      cumulativeNetCents: 0,
      vatOutflowCents: 0,
    });
  }

  if (todayValid) {
    // ── EIN — offene Forderungen je erwartetem Zahl-Datum ────────────────────
    for (const row of input.openItems ?? []) {
      if (!row || row.status === 'paid') continue;
      const amount = row.openAmountCents;
      if (!Number.isFinite(amount) || amount === 0) continue;
      const idx = inflowWeekIndex(row, input.dso, today, horizon);
      if (idx === null) continue;
      weeks[idx].inflowCents += amount;
    }

    // ── AUS(1) — booked-Ausgaben mit dueDate >= heute ────────────────────────
    for (const e of input.expenses ?? []) {
      if (!e || e.status !== 'booked') continue;
      const due = toDateOnly(e.dueDate);
      if (due === null || due < today) continue; // dueDate null/<heute ⇒ zählt NICHT
      if (!Number.isFinite(e.grossCents)) continue;
      const idx = Math.floor(daysBetweenUTC(today, due) / DAYS_PER_WEEK);
      if (idx < 0 || idx >= horizon) continue;
      weeks[idx].outflowCents += e.grossCents;
    }

    // ── AUS(2) — USt-Zahllast am UStVA-Termin (fail-closed) ──────────────────
    if (input.vatReturn !== null) {
      const payable = input.vatReturn.payableCents;
      const deadline = toDateOnly(input.ustvaDeadlineISO);
      // Nur positive Zahllast ist Abfluss; Erstattung (≤0) NICHT als Zufluss buchen.
      if (Number.isFinite(payable) && payable > 0 && deadline !== null) {
        // Überfällige Meldung (Termin < heute) ⇒ imminenter Abfluss in Woche 1.
        const idx = deadline < today ? 0 : Math.floor(daysBetweenUTC(today, deadline) / DAYS_PER_WEEK);
        if (idx >= 0 && idx < horizon) {
          weeks[idx].outflowCents += payable;
          weeks[idx].vatOutflowCents += payable;
        }
      }
    }
  }

  // ── Netto + kumulierte Linie (ab 0) ────────────────────────────────────────
  let cumulative = 0;
  for (const w of weeks) {
    w.netCents = w.inflowCents - w.outflowCents;
    cumulative += w.netCents;
    w.cumulativeNetCents = cumulative;
  }

  return { weeks, horizonWeeks: horizon, dsoShiftDays };
}

/**
 * Summe der `netCents` der ersten `weeks` Wochen (Default 4 ≈ „nächste 30 Tage")
 * — die Kennzahl der Hero-Kachel „Netto-Cash nächste 30 Tage" (§6.2 Hero-1).
 * Entspricht `cumulativeNetCents` der `weeks`-ten Woche (falls vorhanden).
 *
 * @param result - Ergebnis von {@link computeNettoCash}.
 * @param weeks - Anzahl der einbezogenen Anfangswochen (Default 4).
 */
export function sumNetForWeeks(result: NettoCashResult, weeks = 4): number {
  const n = Math.min(weeks, result.weeks.length);
  let sum = 0;
  for (let i = 0; i < n; i++) sum += result.weeks[i].netCents;
  return sum;
}
